import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  CalendarDays,
  MapPin,
  Clock,
  Play,
  Quote,
  Megaphone,
} from "lucide-react";

import { homeQuery, settingsQuery } from "@/lib/queries";
import { useSiteSettings } from "@/lib/use-site-settings";
import { formatDate, videoEmbed } from "@/lib/format";
import { DynamicIcon, Section, SectionTitle, RichText } from "@/components/site/bits";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Professor Rurdiney — 40123 | Educação que transforma" },
      {
        name: "description",
        content:
          "Professor Rurdiney, candidato a vereador pelo número 40123. Educação que transforma, cidade que inclui. Conheça propostas, trabalhos e agenda.",
      },
      {
        property: "og:title",
        content: "Professor Rurdiney — 40123 | Educação que transforma",
      },
      {
        property: "og:description",
        content:
          "Candidato a vereador pelo número 40123. Educação que transforma, cidade que inclui.",
      },
    ],
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(homeQuery()),
  component: HomePage,
});

function HomePage() {
  const { data } = useSuspenseQuery(homeQuery());
  const settings = useSiteSettings();

  const {
    numbers,
    proposals,
    works,
    news,
    events,
    testimonials,
    videos,
    timeline,
  } = data;

  return (
    <>
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden bg-hero-gradient text-primary-foreground">
        {settings.heroBgUrl ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${settings.heroBgUrl})` }}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 bg-hero-gradient opacity-80 mix-blend-multiply"
              aria-hidden="true"
            />
          </>
        ) : null}
        <div className="absolute inset-0 bg-mesh opacity-50" aria-hidden="true" />
        <div className="absolute -right-20 -top-20 size-72 rounded-full bg-accent/20 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 md:py-32">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div className="animate-rise">
              <span className="inline-flex rounded-full bg-accent px-4 py-1.5 text-sm font-bold uppercase tracking-widest text-accent-foreground">
                {settings.office} • Nº {settings.candidateNumber}
              </span>
              <h1 className="mt-6 text-5xl leading-[0.9] md:text-7xl lg:text-8xl">
                {settings.heroTitle || "Professor Rurdiney"}
              </h1>
              <p className="mt-5 max-w-xl text-lg text-primary-foreground/85 md:text-xl">
                {settings.heroTagline}
              </p>
              <p className="mt-2 max-w-xl text-base font-semibold text-primary-foreground">
                {settings.heroSubtitle}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/propostas"
                  className="inline-flex items-center gap-2 rounded-xl bg-accent px-6 py-3 text-sm font-bold uppercase tracking-wide text-accent-foreground shadow-accent transition-transform hover:scale-105"
                >
                  Ver propostas <ArrowRight className="size-4" />
                </Link>
                <Link
                  to="/contato"
                  className="inline-flex items-center gap-2 rounded-xl border-2 border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-white/10"
                >
                  Participe
                </Link>
              </div>
            </div>

            {/* Hero visual */}
            <div className="relative hidden md:block">
              {settings.heroPhotoUrl ? (
                <img
                  src={settings.heroPhotoUrl}
                  alt={settings.candidateName}
                  className="relative mx-auto max-h-[560px] w-full object-contain drop-shadow-2xl"
                />
              ) : (
                <>
                  <div className="absolute inset-0 rotate-3 rounded-3xl bg-accent/30 blur-2xl" aria-hidden="true" />
                  <div className="relative rounded-3xl border border-white/20 bg-white/10 p-8 backdrop-blur-md">
                    <div className="flex items-center gap-4">
                      <span className="flex size-20 items-center justify-center rounded-2xl bg-accent font-display text-4xl text-accent-foreground">
                        R
                      </span>
                      <div>
                        <div className="font-display text-2xl">{settings.candidateName}</div>
                        <div className="text-sm text-primary-foreground/70">
                          {settings.city} — {settings.state}
                        </div>
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-2 gap-4">
                      {numbers.slice(0, 4).map((n: any) => (
                        <div
                          key={n.id}
                          className="rounded-2xl bg-white/10 p-4 text-center"
                        >
                          <div className="font-display text-3xl text-primary-foreground">
                            {n.value}
                            {n.suffix}
                          </div>
                          <div className="mt-1 text-xs uppercase tracking-wide text-primary-foreground/70">
                            {n.label}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

          </div>
        </div>

        {/* Wave divider */}
        <div className="relative">
          <svg
            viewBox="0 0 1440 80"
            className="block w-full"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M0,80 L0,40 Q360,0 720,40 T1440,40 L1440,80 Z" fill="var(--background)" />
          </svg>
        </div>
      </section>

      {/* ===== NUMBERS (mobile) ===== */}
      <Section className="md:hidden -mt-4">
        <div className="grid grid-cols-2 gap-4">
          {numbers.map((n: any) => (
            <div
              key={n.id}
              className="rounded-2xl border border-border bg-card p-5 text-center shadow-card"
            >
              <DynamicIcon name={n.icon} className="mx-auto size-7 text-primary" />
              <div className="mt-2 font-display text-3xl text-foreground">
                {n.value}
                {n.suffix}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">
                {n.label}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== TIMELINE ===== */}
      <Section>
        <SectionTitle
          eyebrow="Trajetória"
          title="Uma vida dedicada à educação"
          description="De professor da rede pública à Universidade Federal do Maranhão, a jornada de quem sempre esteve junto da comunidade."
        />
        <div className="mt-12 grid gap-4 md:grid-cols-3 lg:grid-cols-6">
          {timeline.map((t: any, i: number) => (
            <div
              key={t.id}
              className="rounded-2xl border border-border bg-card p-5 shadow-card"
            >
              <div className="font-display text-2xl text-accent-deep">{t.year}</div>
              <div className="mt-2 text-sm font-semibold text-foreground">{t.title}</div>
              <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {t.description}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/quem-e"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary hover:text-accent-deep"
          >
            Conheça a história completa <ArrowRight className="size-4" />
          </Link>
        </div>
      </Section>

      {/* ===== PROPOSTAS ===== */}
      <Section className="bg-secondary/50">
        <SectionTitle
          eyebrow="Propostas"
          title="Educação que transforma"
          description="Propostas concretas para uma São Luís mais justa, educada e inclusiva."
          align="center"
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {proposals.map((p: any) => (
            <Link
              key={p.id}
              to="/propostas/$slug"
              params={{ slug: p.slug }}
              className="group flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-all hover:border-primary hover:shadow-glow"
            >
              <div className="flex size-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <DynamicIcon name={p.icon} className="size-6" />
              </div>
              <h3 className="mt-4 text-xl text-foreground">{p.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                {p.summary}
              </p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-primary group-hover:text-accent-deep">
                Saiba mais <ArrowRight className="size-4" />
              </span>
            </Link>
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/propostas"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold uppercase tracking-wide text-primary-foreground transition-colors hover:bg-primary-deep"
          >
            Todas as propostas <ArrowRight className="size-4" />
          </Link>
        </div>
      </Section>

      {/* ===== TRABALHOS ===== */}
      <Section>
        <SectionTitle
          eyebrow="Trabalhos realizados"
          title="Quem faz, prova"
          description="Ações concretas que mudaram a realidade de bairros e comunidades de São Luís."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {works.map((w: any) => (
            <Link
              key={w.id}
              to="/trabalhos/$slug"
              params={{ slug: w.slug }}
              className="group overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:shadow-glow"
            >
              <div className="flex items-center gap-3 border-b border-border p-5">
                <DynamicIcon
                  name="Hammer"
                  className="size-5 text-accent-deep"
                />
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {w.location}
                </span>
              </div>
              <div className="p-5">
                <h3 className="text-lg text-foreground">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {w.summary}
                </p>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/trabalhos"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary hover:text-accent-deep"
          >
            Ver todos os trabalhos <ArrowRight className="size-4" />
          </Link>
        </div>
      </Section>

      {/* ===== DEPOIMENTOS ===== */}
      <Section className="bg-primary-deep text-primary-foreground">
        <SectionTitle
          eyebrow="Depoimentos"
          title="A voz da comunidade"
          align="center"
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((t: any) => (
            <div
              key={t.id}
              className="rounded-2xl bg-white/10 p-6 backdrop-blur-sm"
            >
              <Quote className="size-8 text-primary-foreground/70" />
              <p className="mt-4 text-sm leading-relaxed text-primary-foreground/90">
                {t.quote}
              </p>
              <div className="mt-5">
                <div className="font-display text-sm uppercase text-primary-foreground">
                  {t.author_name}
                </div>
                <div className="text-xs text-primary-foreground/60">
                  {t.author_role}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== NOTÍCIAS ===== */}
      <Section>
        <SectionTitle
          eyebrow="Notícias"
          title="Últimas notícias"
          description="Acompanhe a campanha e as ações do Professor Rurdiney."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {news.map((n: any) => (
            <Link
              key={n.id}
              to="/noticias/$slug"
              params={{ slug: n.slug }}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-all hover:shadow-glow"
            >
              <div className="flex-1 p-6">
                <span className="text-xs font-bold uppercase tracking-wide text-accent-deep">
                  {formatDate(n.published_at)}
                </span>
                <h3 className="mt-2 text-lg text-foreground">{n.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {n.excerpt}
                </p>
              </div>
              <div className="border-t border-border px-6 py-3">
                <span className="inline-flex items-center gap-1 text-sm font-bold uppercase tracking-wide text-primary group-hover:text-accent-deep">
                  Ler mais <ArrowRight className="size-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/noticias"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary hover:text-accent-deep"
          >
            Todas as notícias <ArrowRight className="size-4" />
          </Link>
        </div>
      </Section>

      {/* ===== AGENDA ===== */}
      <Section className="bg-secondary/50">
        <SectionTitle
          eyebrow="Agenda"
          title="Próximos eventos"
          description="Venha participar dos nossos eventos pela cidade."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {events.map((e: any) => (
            <div
              key={e.id}
              className="flex flex-col rounded-2xl border border-border bg-card p-6 shadow-card"
            >
              <div className="flex items-center gap-3">
                <span className="flex size-14 flex-col items-center justify-center rounded-xl bg-accent text-accent-foreground">
                  <CalendarDays className="size-5" />
                </span>
                <div>
                  <div className="font-display text-lg text-foreground">{e.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {formatDate(e.event_date)} • {e.event_time}
                  </div>
                </div>
              </div>
              {e.neighborhood && (
                <p className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="size-4 shrink-0" /> {e.neighborhood}
                </p>
              )}
              {e.description && (
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {e.description}
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/agenda"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary hover:text-accent-deep"
          >
            Agenda completa <ArrowRight className="size-4" />
          </Link>
        </div>
      </Section>

      {/* ===== VÍDEOS ===== */}
      {videos.length > 0 && (
        <Section>
          <SectionTitle
            eyebrow="Vídeos"
            title="Assista"
            description="Vídeos da campanha e das ações comunitárias."
          />
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {videos.slice(0, 3).map((v: any) => {
              const embed = videoEmbed(v.video_url);
              return (
                <article
                  key={v.id}
                  className="overflow-hidden rounded-2xl border border-border bg-card text-left shadow-card"
                >
                  {embed ? (
                    <div
                      className={
                        embed.provider === "instagram"
                          ? "aspect-[4/5] w-full bg-background"
                          : "aspect-video w-full bg-primary-deep"
                      }
                    >
                      <iframe
                        src={embed.src}
                        title={v.title}
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        scrolling="no"
                        frameBorder={0}
                        className="size-full"
                      />
                    </div>
                  ) : (
                    <a
                      href={v.video_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex aspect-video w-full items-center justify-center bg-primary-deep text-primary-foreground"
                    >
                      <Play className="size-10" />
                    </a>
                  )}
                  <div className="p-5">
                    <h3 className="text-lg text-foreground">{v.title}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{v.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/videos"
              className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary hover:text-accent-deep"
            >
              Ver todos os vídeos <ArrowRight className="size-4" />
            </Link>
          </div>

        </Section>
      )}

      {/* ===== CTA ===== */}
      <section className="bg-accent-gradient px-4 py-20 text-center">
        <div className="mx-auto max-w-3xl">
          <Megaphone className="mx-auto size-12 text-accent-foreground" />
          <h2 className="mt-6 text-4xl text-accent-foreground md:text-5xl">
            Junte-se a nós
          </h2>
          <p className="mt-4 text-lg text-accent-foreground/80">
            Participe da campanha, contribua com suas ideias e ajude a transformar São Luís.
          </p>
          <Link
            to="/contato"
            className="mt-8 inline-flex items-center gap-2 rounded-xl bg-primary-deep px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground shadow-glow transition-transform hover:scale-105"
          >
            Quero participar <ArrowRight className="size-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
